"use client";

// ============================================================
// CLUBHUB – Nachrichten: der Editor
// ============================================================
// EINE Flaeche in drei Zonen — 1 Empfaenger, 2 Inhalt, 3 Vorschau.
// Auf breiten Schirmen steht die Empfaenger-Zone oben ueber die
// volle Breite (mehrspaltig), darunter Inhalt und Vorschau
// nebeneinander, gleich hoch und oben buendig: was man schreibt,
// steht auf derselben Hoehe wie das, was ankommt. Die Vorschau
// laeuft mit jeder Eingabe mit und bleibt bei langen Texten im Bild.
//
// Sind die Empfaenger gewaehlt, klappt man die Zone auf ihren Kopf
// zusammen (ein Satz, wer was bekommt) und hat Text und Vorschau
// ganz im Blick. Ein Entwurf, der schon Empfaenger hat (Kopie,
// „Nachricht an …“ aus Mitglieder oder Gruppen), oeffnet so.
//
// Der Versand sitzt in einer Leiste, die unten am Fenster klebt:
// wie viele auf welchem Weg, Test, Planen, Senden — von ueberall
// erreichbar. Auf dem Telefon sind es drei Schritte mit Reitern
// oben; dieselbe Leiste fuehrt mit „Weiter“ zum naechsten.
//
// Derselbe Editor schreibt auch VORLAGEN (`modus="vorlage"`): dort
// steht an der Stelle der Empfaenger die Angabe zur Vorlage, und die
// Vorschau blaettert durch die Mitglieder.
//
// ENTWUERFE SPEICHERN SICH SELBST — kurz nach jeder Aenderung, mit
// dem Hinweis „gespeichert“ oben. Einen Knopf „Speichern“ gibt es
// fuer Nachrichten nicht; wer die Seite verlaesst, verliert nichts.
// In der Maskenphase haelt der Browser den Entwurf
// (`localStorage`); die spaetere Backend-Stelle ist markiert.
// ============================================================
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import {
  CalendarClock,
  Check,
  ChevronRight,
  ChevronUp,
  CloudUpload,
  FlaskConical,
  MoreHorizontal,
  Pencil,
  Send,
  Trash2,
  Undo2,
} from "lucide-react";
import type { MailingChannel, MailingStatus, TemplateKind } from "@/generated/prisma/enums";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { DropdownItem, DropdownMenu, DropdownSeparator } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Select, type SelectOption } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { PLAETTCHEN, PLAETTCHEN_AN, PLAETTCHEN_AUS, Schiene } from "@/components/liste";
import { TEMPLATE_KIND } from "@/lib/labels";
import { cn, formatDateTime } from "@/lib/utils";
import {
  aufloesen,
  auswahlSatz,
  MITGLIEDS_STATUS,
  type Empfaenger,
} from "@/lib/nachrichten/empfaenger";
import { unbekannte } from "@/lib/nachrichten/platzhalter";
import { ARTEN_FUER_KANAL, kanalFuerArt, VORLAGEN_ARTEN } from "@/lib/nachrichten/ansicht";
import type {
  EntwurfView,
  TemplateView,
  VereinsangabenView,
  VorlagenEntwurfView,
} from "@/lib/nachrichten/views";
import type { ContactView, FeeTypeView, GroupView } from "@/lib/views";
import { EmpfaengerZone, KanalWahl } from "./empfaenger-zone";
import { InhaltZone } from "./inhalt-zone";
import { VorschauZone } from "./vorschau-zone";
import { PlanenDialog } from "./planen-dialog";
import { KANAL_ICON, MailingStatusBadge, WEG_LABEL } from "./kanal";
import { Angabe, Zone } from "./zone";

type ZoneId = "eins" | "zwei" | "drei";

type Gemeinsam = {
  verein: VereinsangabenView;
  kontakte: ContactView[];
  gruppen: GroupView[];
  beitraege: FeeTypeView[];
  vorlagen: TemplateView[];
  ich: { name: string; email: string };
  zurueck: { href: string; label: string };
};

type NachrichtModus = Gemeinsam & {
  modus: "nachricht";
  start: EntwurfView;
  antwortOptionen: SelectOption[];
  /** Einen im Browser liegengebliebenen Entwurf wiederherstellen? */
  wiederherstellen: boolean;
  titel: string;
};

type VorlageModus = Gemeinsam & {
  modus: "vorlage";
  start: VorlagenEntwurfView;
  titel: string;
};

type Fehler = { empfaenger?: string; subject?: string; body?: string; name?: string };

const SPEICHER = "clubhub:nachrichten:entwurf:";

type VorlagenAngaben = { name: string; kind: TemplateKind; isDefault: boolean };

/** Ein Stand des Editors als Text — zum Vergleichen, ob etwas offen ist. */
function schnappschuss(e: EntwurfView, v: VorlagenAngaben): string {
  return JSON.stringify({ e, v });
}

const KANALFREI: TemplateKind[] = ["WELCOME", "DUNNING", "DONATION_RECEIPT", "INVOICE_TEXT"];

function artFuerKanal(kind: TemplateKind, channel: MailingChannel): TemplateKind {
  if (channel === "SMS") return "SMS";
  if (KANALFREI.includes(kind)) return kind;
  return channel === "LETTER" ? "LETTER" : "EMAIL";
}

function ausVorlage(v: VorlagenEntwurfView, verein: VereinsangabenView): EntwurfView {
  return {
    id: v.id,
    status: "DRAFT",
    channel: kanalFuerArt(v.kind),
    subject: v.subject,
    body: v.body,
    fromName: verein.absenderName,
    replyTo: verein.email,
    audience: { groups: [], statuses: [], include: [], exclude: [], purpose: "INFO" },
    attachments: [],
    templateId: null,
    scheduledAt: null,
    updatedAt: v.updatedAt,
  };
}

export function NachrichtEditor(props: NachrichtModus | VorlageModus) {
  const router = useRouter();
  const { verein, kontakte, gruppen, beitraege, vorlagen, ich } = props;
  const istVorlage = props.modus === "vorlage";

  const [anfang] = useState(() => ({
    e: props.modus === "vorlage" ? ausVorlage(props.start, verein) : props.start,
    v:
      props.modus === "vorlage"
        ? { name: props.start.name, kind: props.start.kind, isDefault: props.start.isDefault }
        : { name: "", kind: "EMAIL" as TemplateKind, isDefault: false },
  }));
  const [entwurf, setEntwurf] = useState<EntwurfView>(anfang.e);
  const [vorlage, setVorlage] = useState<{ name: string; kind: TemplateKind; isDefault: boolean }>(
    anfang.v,
  );
  const [zone, setZone] = useState<ZoneId>("eins");
  const [index, setIndex] = useState(0);
  const [geraet, setGeraet] = useState<"desktop" | "telefon">("desktop");
  const [versucht, setVersucht] = useState(false);
  const [planen, setPlanen] = useState(false);
  // Mit Empfaengern geoeffnet: die Zone steht zugeklappt, der Text vorn.
  const [empfaengerZu, setEmpfaengerZu] = useState(() => {
    const a = anfang.e.audience;
    return !istVorlage && a.groups.length + a.statuses.length + a.include.length > 0;
  });
  // Der zuletzt gespeicherte Stand: was davon abweicht, ist offen.
  const [gespeichert, setGespeichert] = useState<{ als: string; um: string | null }>(() => ({
    als: schnappschuss(anfang.e, anfang.v),
    um: props.start.updatedAt,
  }));

  const setze = (teil: Partial<EntwurfView>) => setEntwurf((e) => ({ ...e, ...teil }));
  const speicherSchluessel = `${SPEICHER}${props.start.id ?? "neu"}`;

  // ── Wer bekommt sie? ──────────────────────────────────────
  const aufloesung = useMemo(
    () => aufloesen(entwurf.audience, entwurf.channel, kontakte, gruppen),
    [entwurf.audience, entwurf.channel, kontakte, gruppen],
  );
  const mitglieder = useMemo(
    () => kontakte.filter((c) => MITGLIEDS_STATUS.includes(c.status)),
    [kontakte],
  );
  const vorschauPersonen: Empfaenger[] = useMemo(() => {
    if (!istVorlage && aufloesung.erreicht.length) return aufloesung.erreicht;
    if (!istVorlage) return [];
    // Vorlage: durch die Mitglieder blaettern, auf dem Weg der Vorlage.
    const weg =
      entwurf.channel === "SMS" ? "SMS" : entwurf.channel === "LETTER" ? "LETTER" : "EMAIL";
    return mitglieder.map((kontakt) => ({
      kontakt,
      weg,
      grund: null,
      briefgrund: null,
      quelle: "STATUS" as const,
    }));
  }, [istVorlage, aufloesung, mitglieder, entwurf.channel]);

  // ── Entwurf speichert sich selbst ─────────────────────────
  const jetztStand = schnappschuss(entwurf, vorlage);
  const offen = jetztStand !== gespeichert.als;
  const standArt: "neu" | "speichert" | "gespeichert" | "geaendert" = offen
    ? istVorlage
      ? "geaendert"
      : "speichert"
    : gespeichert.um
      ? "gespeichert"
      : "neu";

  useEffect(() => {
    if (props.modus !== "nachricht" || !props.wiederherstellen) return;
    const start = props.start;
    // Nach dem ersten Bild: was der Browser haelt, kennt der Server nicht.
    const uhr = setTimeout(() => {
      try {
        const roh = localStorage.getItem(speicherSchluessel);
        if (!roh) return;
        const { entwurf: alt, zeit } = JSON.parse(roh) as { entwurf: EntwurfView; zeit: string };
        if (schnappschuss(alt, anfang.v) === schnappschuss(start, anfang.v)) return;
        setEntwurf(alt);
        setGespeichert({ als: schnappschuss(alt, anfang.v), um: zeit });
        toast(`Entwurf vom ${formatDateTime(zeit)} wiederhergestellt`, {
          action: {
            label: "Verwerfen",
            onClick: () => {
              localStorage.removeItem(speicherSchluessel);
              setEntwurf(start);
              setGespeichert({ als: schnappschuss(start, anfang.v), um: start.updatedAt });
            },
          },
        });
      } catch {
        // Ein unlesbarer Entwurf wird uebergangen.
      }
    }, 0);
    return () => clearTimeout(uhr);
    // Nur beim Oeffnen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Vorlagen speichert man ausdruecklich — der Hinweis sagt nur, dass etwas offen ist.
    if (istVorlage || !offen) return;
    const uhr = setTimeout(() => {
      const zeit = new Date().toISOString();
      // BACKEND: saveDraft(entwurf) — legt beim ersten Mal das Mailing (DRAFT) an, danach ein Feld-Update; liefert id und updatedAt
      try {
        localStorage.setItem(speicherSchluessel, JSON.stringify({ entwurf, zeit }));
      } catch {
        // Voller Speicher: der Browser vergisst, der Server (spaeter) nicht.
      }
      setGespeichert({ als: jetztStand, um: zeit });
    }, 700);
    return () => clearTimeout(uhr);
  }, [entwurf, jetztStand, offen, istVorlage, speicherSchluessel]);

  // ── Pruefen ────────────────────────────────────────────────
  const fehler: Fehler = useMemo(() => {
    const f: Fehler = {};
    if (!istVorlage && aufloesung.zahlen.gesamt === 0)
      f.empfaenger = "Wähle mindestens eine Gruppe, einen Status oder eine Person.";
    if (istVorlage && !vorlage.name.trim()) f.name = "Die Vorlage braucht einen Namen.";
    if (!entwurf.subject.trim() && !(istVorlage && entwurf.channel === "SMS")) {
      f.subject =
        entwurf.channel === "SMS"
          ? "Gib der SMS eine Bezeichnung für die Liste."
          : entwurf.channel === "LETTER"
            ? "Der Brief braucht einen Betreff."
            : "Ohne Betreff geht keine Mail hinaus.";
    }
    const falsch = unbekannte(`${entwurf.subject}\n${entwurf.body}`);
    if (!entwurf.body.trim()) f.body = "Der Text fehlt noch.";
    else if (falsch.length) f.body = `Unbekannter Platzhalter: ${falsch.join(", ")}`;
    return f;
  }, [entwurf, vorlage.name, istVorlage, aufloesung.zahlen.gesamt]);
  const sichtbar: Fehler = versucht ? fehler : {};
  const zoneFehler: Record<ZoneId, boolean> = {
    eins: Boolean(sichtbar.empfaenger || sichtbar.name),
    zwei: Boolean(sichtbar.subject || sichtbar.body),
    drei: false,
  };

  function gueltig(): boolean {
    setVersucht(true);
    if (Object.keys(fehler).length === 0) return true;
    const ziel: ZoneId = fehler.empfaenger || fehler.name ? "eins" : "zwei";
    setZone(ziel);
    if (ziel === "eins") setEmpfaengerZu(false);
    document.getElementById(`zone-${ziel}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    toast.error("Bitte prüfe die markierten Angaben.");
    return false;
  }

  // ── Handlungen ─────────────────────────────────────────────
  const z = aufloesung.zahlen;
  const wegeSatz = [
    z.email && `${z.email} per Mail`,
    z.sms && `${z.sms} per SMS`,
    z.brief && `${z.brief} per Brief`,
  ]
    .filter(Boolean)
    .join(", ");

  async function senden() {
    if (!gueltig()) return;
    const raus = z.ausgeschlossen;
    const ok = await confirmDialog({
      title: `Jetzt an ${z.gesamt} Empfänger senden?`,
      description: `${wegeSatz}.${z.brief ? " Briefe gehen an den Druck- und Versanddienst." : ""}${raus ? ` ${raus} ${raus === 1 ? "Person wird" : "Personen werden"} nicht angeschrieben.` : ""} Ein Versand lässt sich nicht zurückholen.`,
      confirmLabel: "Jetzt senden",
    });
    if (!ok) return;
    // BACKEND: sendMailing(entwurf) — Mailing auf SENDING, MailingRecipient je Empfaenger (mit Weg), Mails an den Versanddienst, Briefe an den Druckdienst
    localStorage.removeItem(speicherSchluessel);
    toast.success("Die Nachricht geht hinaus", {
      description: `${wegeSatz}. Wer sie bekommen hat, steht im Versandprotokoll.`,
    });
    router.push("/nachrichten");
  }

  function geplant(zeitpunkt: string) {
    setPlanen(false);
    // BACKEND: scheduleMailing(entwurf, zeitpunkt) — Mailing auf SCHEDULED, der Versand-Worker startet zum Zeitpunkt
    localStorage.removeItem(speicherSchluessel);
    toast.success(`Versand geplant für ${formatDateTime(zeitpunkt)}`, {
      description: `${z.gesamt} Empfänger · ${wegeSatz}`,
    });
    router.push("/nachrichten");
  }

  async function planungAufheben() {
    const ok = await confirmDialog({
      title: "Planung aufheben?",
      description: "Die Nachricht bleibt als Entwurf erhalten und geht nicht hinaus.",
      confirmLabel: "Planung aufheben",
    });
    if (!ok) return;
    // BACKEND: unscheduleMailing(id) — Mailing zurueck auf DRAFT
    setze({ status: "DRAFT", scheduledAt: null });
    toast.success("Planung aufgehoben — die Nachricht ist wieder ein Entwurf.");
  }

  function testversand() {
    setVersucht(true);
    if (fehler.subject || fehler.body) {
      gueltig();
      return;
    }
    const person =
      vorschauPersonen[Math.min(index, vorschauPersonen.length - 1)]?.kontakt ?? mitglieder[0];
    // BACKEND: sendTest(entwurf, person.id, ich.email) — genau eine Nachricht an mich, mit den Werten dieser Person
    if (entwurf.channel === "LETTER")
      toast.success("Probedruck erstellt", {
        description: `PDF mit den Angaben von ${person?.displayName ?? "einer Musterperson"}.`,
      });
    else
      toast.success(
        entwurf.channel === "SMS"
          ? "Test-SMS an dich verschickt"
          : `Testmail an ${ich.email} verschickt`,
        {
          description: `Mit den Angaben von ${person?.displayName ?? "einer Musterperson"}.`,
        },
      );
  }

  async function loeschen() {
    const ok = await confirmDialog({
      title: istVorlage ? `Vorlage „${vorlage.name}“ löschen?` : "Entwurf löschen?",
      description: istVorlage
        ? "Nachrichten, die daraus entstanden sind, bleiben unverändert."
        : "Der Entwurf verschwindet mit Empfängerauswahl und Anhängen.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    // BACKEND: deleteMailing(id) bzw. deleteTemplate(id)
    localStorage.removeItem(speicherSchluessel);
    toast.success(istVorlage ? "Vorlage gelöscht" : "Entwurf gelöscht");
    router.push(istVorlage ? "/nachrichten/vorlagen" : "/nachrichten");
  }

  function vorlageSpeichern() {
    if (!gueltig()) return;
    // BACKEND: saveTemplate({ id, kind, name, subject, body, isDefault }) — MessageTemplate anlegen oder aendern
    setGespeichert({ als: jetztStand, um: new Date().toISOString() });
    toast.success(props.start.id ? "Vorlage gespeichert" : "Vorlage angelegt", {
      description: vorlage.name,
    });
    if (!props.start.id) router.push("/nachrichten/vorlagen");
  }

  async function vorlageAnwenden(id: string) {
    const v = vorlagen.find((x) => x.id === id);
    if (!v) return;
    if (
      (entwurf.subject.trim() || entwurf.body.trim()) &&
      !(await confirmDialog({
        title: `Text durch „${v.name}“ ersetzen?`,
        description: "Betreff und Text werden überschrieben; Empfänger und Anhänge bleiben.",
        confirmLabel: "Ersetzen",
      }))
    )
      return;
    setze({ templateId: v.id, subject: v.subject ?? entwurf.subject, body: v.body });
    toast.success("Vorlage übernommen");
  }

  function kanalWechsel(channel: MailingChannel) {
    setze({
      channel,
      templateId:
        entwurf.templateId &&
        ARTEN_FUER_KANAL[channel].includes(
          vorlagen.find((v) => v.id === entwurf.templateId)?.kind ?? "EMAIL",
        )
          ? entwurf.templateId
          : null,
    });
  }

  // ── Zeichnen ───────────────────────────────────────────────
  const vorlagenOptionen: SelectOption[] = vorlagen
    .filter((v) => ARTEN_FUER_KANAL[entwurf.channel].includes(v.kind))
    .map((v) => ({ value: v.id, label: v.name, hint: TEMPLATE_KIND[v.kind] }));
  const antwortOptionen = props.modus === "nachricht" ? props.antwortOptionen : [];

  const standText =
    standArt === "neu"
      ? istVorlage
        ? "Noch nicht gespeichert"
        : "Speichert sich selbst"
      : standArt === "speichert"
        ? "Wird gespeichert …"
        : standArt === "geaendert"
          ? "Ungespeicherte Änderungen"
          : `Gespeichert ${formatDateTime(gespeichert.um)}`;
  const Standzeichen =
    standArt === "speichert" ? CloudUpload : standArt === "gespeichert" ? Check : null;
  const standZeile = (
    <span
      className={cn(
        "text-caption numeric inline-flex items-center gap-1.5",
        standArt === "geaendert" ? "text-warning" : "text-text-tertiary",
      )}
    >
      {Standzeichen && <Standzeichen className="h-3.5 w-3.5" aria-hidden />}
      {standText}
    </span>
  );

  const status: MailingStatus = entwurf.status;
  const zonen: { id: ZoneId; label: string; zahl?: number }[] = [
    {
      id: "eins",
      label: istVorlage ? "Vorlage" : "Empfänger",
      zahl: istVorlage ? undefined : z.gesamt,
    },
    { id: "zwei", label: "Inhalt" },
    { id: "drei", label: "Vorschau" },
  ];

  function zuDenEmpfaengern() {
    setEmpfaengerZu(false);
    setZone("eins");
    requestAnimationFrame(() =>
      document.getElementById("zone-eins")?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  }

  const KanalIcon = KANAL_ICON[entwurf.channel];
  const hatAuswahl =
    entwurf.audience.groups.length +
      entwurf.audience.statuses.length +
      entwurf.audience.include.length >
    0;
  const auswahl = auswahlSatz(entwurf.audience, gruppen, kontakte);

  // Kopf der Empfaenger-Zone: zugeklappt steht dort, was gilt.
  const empfaengerKopf = !istVorlage && (empfaengerZu || hatAuswahl) && (
    <div className="flex items-center gap-3 max-lg:hidden">
      {empfaengerZu && (
        <span className="text-caption text-text-secondary flex items-center gap-1.5 whitespace-nowrap">
          <KanalIcon className="text-text-tertiary h-3.5 w-3.5" aria-hidden />
          {WEG_LABEL[entwurf.channel]} ·{" "}
          {entwurf.audience.purpose === "NEWSLETTER"
            ? "Newsletter oder Werbung"
            : "Vereinsinformation"}
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => (empfaengerZu ? zuDenEmpfaengern() : setEmpfaengerZu(true))}
        aria-expanded={!empfaengerZu}
        aria-controls="zone-eins"
      >
        {empfaengerZu ? (
          <Pencil className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
        )}
        {empfaengerZu ? "Ändern" : "Zuklappen"}
      </Button>
    </div>
  );

  // Die Leiste unten: wer bekommt es, und los. Auf dem Telefon in
  // Schritt 1 und 2 fuehrt sie weiter; Vorlagen speichert der Kopf.
  const leiste = (!istVorlage || zone !== "drei") && (
    <div
      className={cn(
        "bg-bg-elevated shadow-e3 sticky z-30 -mx-1 flex items-center gap-3 rounded-xl px-4 py-2.5 ring-1 ring-black/[0.04] dark:ring-white/[0.06]",
        "bottom-[calc(3.75rem+env(safe-area-inset-bottom))] lg:bottom-4 lg:mx-0 lg:py-3",
        istVorlage && "lg:hidden",
      )}
    >
      <div className="min-w-0 flex-1">
        {istVorlage ? (
          standZeile
        ) : (
          <>
            <p className="text-body-sm text-text-secondary numeric truncate">
              {z.gesamt > 0 ? (
                <>
                  <span className="max-sm:hidden">An </span>
                  <strong className="text-text-primary font-medium">{z.gesamt}</strong> Empfänger
                  <span className="max-sm:hidden"> · {wegeSatz}</span>
                </>
              ) : (
                <button
                  type="button"
                  onClick={zuDenEmpfaengern}
                  className="hover:text-accent underline-offset-2 hover:underline"
                >
                  Noch keine Empfänger gewählt
                </button>
              )}
            </p>
            {status === "SCHEDULED" && entwurf.scheduledAt ? (
              <p className="text-caption text-text-tertiary flex items-center gap-1.5 truncate">
                <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                Geplant für {formatDateTime(entwurf.scheduledAt)}
              </p>
            ) : (
              <p className="max-lg:hidden">{standZeile}</p>
            )}
          </>
        )}
      </div>
      {zone !== "drei" && (
        <Button
          size="sm"
          className="lg:hidden"
          onClick={() => setZone(zone === "eins" ? "zwei" : "drei")}
        >
          Weiter: {zone === "eins" ? "Inhalt" : "Vorschau"}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      )}
      {!istVorlage && (
        <div className={cn("flex shrink-0 items-center gap-2", zone !== "drei" && "max-lg:hidden")}>
          <Button
            variant="ghost"
            onClick={testversand}
            title={
              entwurf.channel === "LETTER"
                ? "Probedruck"
                : entwurf.channel === "SMS"
                  ? "Test-SMS an mich"
                  : "Testmail an mich"
            }
          >
            <FlaskConical className="h-4 w-4" aria-hidden />
            <span className="max-xl:sr-only">
              {entwurf.channel === "LETTER"
                ? "Probedruck"
                : entwurf.channel === "SMS"
                  ? "Test-SMS an mich"
                  : "Testmail an mich"}
            </span>
          </Button>
          <Button variant="outline" onClick={() => (gueltig() ? setPlanen(true) : undefined)}>
            <CalendarClock className="h-4 w-4" aria-hidden />
            <span className="max-sm:sr-only">
              {status === "SCHEDULED" ? "Neu planen" : "Planen"}
            </span>
          </Button>
          <Button onClick={senden}>
            <Send className="h-4 w-4" aria-hidden />
            <span className="sm:hidden">Senden</span>
            <span className="max-sm:hidden">Jetzt senden</span>
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-[1440px] space-y-5">
      <PageHeader
        title={props.titel}
        back={props.zurueck}
        badge={!istVorlage ? <MailingStatusBadge status={status} /> : undefined}
        description={standZeile}
        actions={
          <>
            {istVorlage && (
              <Button onClick={vorlageSpeichern}>
                <Check className="h-4 w-4" aria-hidden />
                {props.start.id ? "Speichern" : "Vorlage anlegen"}
              </Button>
            )}
            {(props.start.id || status === "SCHEDULED") && (
              <DropdownMenu
                trigger={
                  <Button variant="ghost" size="icon" aria-label="Weitere Aktionen">
                    <MoreHorizontal className="h-4 w-4" aria-hidden />
                  </Button>
                }
              >
                {istVorlage && props.start.id && (
                  <DropdownItem
                    onClick={() => router.push(`/nachrichten/neu?vorlage=${props.start.id}`)}
                  >
                    <Send className="h-4 w-4" aria-hidden />
                    Neue Nachricht damit
                  </DropdownItem>
                )}
                {!istVorlage && status === "SCHEDULED" && (
                  <DropdownItem onClick={planungAufheben}>
                    <Undo2 className="h-4 w-4" aria-hidden />
                    Planung aufheben
                  </DropdownItem>
                )}
                {props.start.id && (
                  <>
                    <DropdownSeparator />
                    <DropdownItem variant="danger" onClick={loeschen}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                      {istVorlage ? "Vorlage löschen" : "Entwurf löschen"}
                    </DropdownItem>
                  </>
                )}
              </DropdownMenu>
            )}
          </>
        }
      />

      {/* Auf dem Telefon: die drei Zonen als Schritte. */}
      <div className="bg-bg-secondary sticky top-[var(--ans-topbar)] z-20 -mx-4 px-4 py-2 lg:hidden">
        <Schiene className="w-full">
          {zonen.map((zn, i) => (
            <button
              key={zn.id}
              type="button"
              aria-pressed={zone === zn.id}
              onClick={() => setZone(zn.id)}
              className={cn(
                PLAETTCHEN,
                "text-body-sm flex-1 justify-center gap-1.5 px-2",
                zone === zn.id ? cn(PLAETTCHEN_AN, "font-medium") : PLAETTCHEN_AUS,
              )}
            >
              <span className="text-text-tertiary numeric">{i + 1}</span>
              {zn.label}
              {zn.zahl !== undefined && (
                <span className="text-caption text-text-tertiary numeric">{zn.zahl}</span>
              )}
              {zoneFehler[zn.id] && (
                <span className="bg-danger h-1.5 w-1.5 rounded-full" aria-label="Angaben fehlen" />
              )}
            </button>
          ))}
        </Schiene>
      </div>

      <Zone
        id="zone-eins"
        nummer={1}
        titel={istVorlage ? "Vorlage" : "Empfänger"}
        beschreibung={
          istVorlage ? (
            "Name und Art — die Vorlage steht dann im Editor zur Wahl."
          ) : empfaengerZu ? (
            <span className="numeric">
              {auswahl}
              <span className="max-lg:hidden">
                {" "}
                · {z.gesamt > 0 ? `${z.gesamt} Empfänger: ${wegeSatz}` : "niemand erreichbar"}
              </span>
            </span>
          ) : (
            auswahl
          )
        }
        rechts={empfaengerKopf}
        zu={empfaengerZu}
        className={cn(zone !== "eins" && "max-lg:hidden")}
      >
        {istVorlage ? (
          <>
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,16rem)_auto]">
              <Input
                id="vorlage-name"
                label="Name der Vorlage"
                value={vorlage.name}
                onChange={(e) => setVorlage((v) => ({ ...v, name: e.target.value }))}
                error={sichtbar.name}
                placeholder="z. B. Einladung zum Saisonabschluss"
                maxLength={80}
              />
              <Select
                id="vorlage-art"
                label="Art"
                options={VORLAGEN_ARTEN.map((k) => ({ value: k, label: TEMPLATE_KIND[k] }))}
                value={vorlage.kind}
                onChange={(k) => {
                  const kind = k as TemplateKind;
                  setVorlage((v) => ({ ...v, kind }));
                  setze({ channel: kanalFuerArt(kind) });
                }}
                hint={
                  vorlage.kind === "DUNNING" || vorlage.kind === "INVOICE_TEXT"
                    ? "Wird von Rechnungen und Mahnungen verwendet."
                    : vorlage.kind === "DONATION_RECEIPT"
                      ? "Begleittext zur Zuwendungsbestätigung."
                      : vorlage.kind === "WELCOME"
                        ? "Wird bei der Aufnahme eines Mitglieds vorgeschlagen."
                        : undefined
                }
              />
              <Angabe titel="Kanal">
                <KanalWahl
                  wert={entwurf.channel}
                  onChange={(channel) => {
                    setze({ channel });
                    // Die Art folgt dem Kanal, wo sie an ihm haengt; Begruessung,
                    // Mahnung und Spendendank gibt es als Mail wie als Brief.
                    setVorlage((v) => ({ ...v, kind: artFuerKanal(v.kind, channel) }));
                  }}
                />
              </Angabe>
            </div>
            <Switch
              checked={vorlage.isDefault}
              onChange={(isDefault) => setVorlage((v) => ({ ...v, isDefault }))}
              label="Als Standard für diese Art vorschlagen"
              hint="Steht beim Anlegen einer passenden Nachricht schon im Feld."
            />
          </>
        ) : (
          <EmpfaengerZone
            channel={entwurf.channel}
            audience={entwurf.audience}
            aufloesung={aufloesung}
            kontakte={kontakte}
            gruppen={gruppen}
            fehler={sichtbar.empfaenger}
            onChannel={kanalWechsel}
            onAudience={(audience) => {
              setze({ audience });
              setIndex(0);
            }}
          />
        )}
      </Zone>

      {/* Inhalt und Vorschau: nebeneinander, gleich hoch, oben buendig. */}
      <div className="grid gap-5 xl:grid-cols-2">
        <Zone
          id="zone-zwei"
          nummer={2}
          titel="Inhalt"
          beschreibung={
            entwurf.channel === "SMS"
              ? "Kurz und ohne Auszeichnung — Platzhalter gehen."
              : "Fett, kursiv, Verweise und Listen über die Leiste; Platzhalter werden je Empfänger ersetzt."
          }
          fuellen
          className={cn(zone !== "zwei" && "max-lg:hidden")}
        >
          <InhaltZone
            modus={props.modus}
            channel={entwurf.channel}
            subject={entwurf.subject}
            body={entwurf.body}
            fromName={entwurf.fromName}
            replyTo={entwurf.replyTo}
            attachments={entwurf.attachments}
            templateId={entwurf.templateId}
            vorlagenOptionen={vorlagenOptionen}
            antwortOptionen={antwortOptionen}
            verein={verein}
            fehler={sichtbar}
            onVorlage={vorlageAnwenden}
            onSubject={(subject) => setze({ subject })}
            onBody={(body) => setze({ body })}
            onFromName={(fromName) => setze({ fromName })}
            onReplyTo={(replyTo) => setze({ replyTo })}
            onAttachments={(attachments) => setze({ attachments })}
          />
        </Zone>

        <Zone
          id="zone-drei"
          nummer={3}
          titel="Vorschau"
          beschreibung={
            istVorlage
              ? "Mit den Angaben eines Mitglieds."
              : "So kommt die Nachricht bei jedem Empfänger an."
          }
          fuellen
          className={cn(zone !== "drei" && "max-lg:hidden")}
        >
          <VorschauZone
            channel={entwurf.channel}
            zweck={entwurf.audience.purpose}
            subject={entwurf.subject}
            body={entwurf.body}
            fromName={entwurf.fromName}
            replyTo={entwurf.channel === "EMAIL" ? entwurf.replyTo : null}
            attachments={entwurf.attachments}
            personen={vorschauPersonen}
            index={index}
            onIndex={setIndex}
            geraet={geraet}
            onGeraet={setGeraet}
            verein={verein}
            beitraege={beitraege}
            musterperson={mitglieder[0]}
          />
        </Zone>
      </div>

      {leiste}

      {planen && (
        <PlanenDialog
          offen
          onClose={() => setPlanen(false)}
          vorgabe={entwurf.scheduledAt}
          empfaenger={z.gesamt}
          onPlanen={geplant}
        />
      )}
    </div>
  );
}
